import { useMemo, useState, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { useGroups } from '@/lib/hooks/useGroups';
import { useRoleDirectory } from '@/lib/hooks/useRoleDirectory';
import { parseCSV, generateTemplateCSV } from '@/lib/utils/csv.utils';
import { getRoleLabelByKey } from '@/lib/utils/roles.utils';
import { useBulkCreateUsers } from '@/lib/hooks/useUserProfile';
import { ManageUsersContent } from '@/content/ManageUsersContent';
import type {
  ParsedCSVRow,
  BulkCreateResult,
} from '@/lib/types/bulk-import.types';
import type { UserRole } from '@/lib/types/database';
import {
  DownloadSimpleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react';

/**
 * Page component for bulk importing users via CSV
 */
export function BulkImport() {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedCSVRow[]>([]);
  const [defaultRole, setDefaultRole] = useState<UserRole | ''>('');
  const [importResult, setImportResult] = useState<BulkCreateResult | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bulkCreateUsersMutation = useBulkCreateUsers();
  const groupsQuery = useGroups(true);
  const { options: roleOptions } = useRoleDirectory();
  const { toast, showToast, hideToast } = useToastNotification();

  const previewRows = useMemo(() => {
    const groupByKey = new Map(
      (groupsQuery.data ?? []).map((group) => [group.import_key, group])
    );

    return parsedRows.map((row) => {
      const groupErrors =
        row.groupImportKeys.length > 0 && groupsQuery.isLoading
          ? ['Group import key validation is still loading']
          : row.groupImportKeys.flatMap((key) => {
              const group = groupByKey.get(key);
              if (!group) {
                return [`Row ${row._rowNumber}: ${key} is not a group key`];
              }
              if (group.archived_at) {
                return [`Row ${row._rowNumber}: ${key} is archived`];
              }
              return [];
            });

      return {
        ...row,
        _errors: [...row._errors, ...groupErrors],
      };
    });
  }, [groupsQuery.data, groupsQuery.isLoading, parsedRows]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      showToast({
        title: 'Invalid file',
        description: ManageUsersContent.bulkImport.errors.invalidFile,
        variant: 'error',
      });
      return;
    }

    setSelectedFile(file);
    setImportResult(null);

    try {
      const rows = await parseCSV(file);
      setParsedRows(rows);
    } catch (error) {
      showToast({
        title: 'Parse error',
        description:
          error instanceof Error
            ? error.message
            : ManageUsersContent.bulkImport.errors.parseError,
        variant: 'error',
      });
      setSelectedFile(null);
      setParsedRows([]);
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = generateTemplateCSV();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'user-import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (parsedRows.length === 0) return;

    // Validate default role is selected
    if (!defaultRole) {
      showToast({
        title: 'Role required',
        description: 'Please select a default role before importing',
        variant: 'error',
      });
      return;
    }

    // Filter out invalid rows
    const validRows = previewRows.filter((row) => row._errors.length === 0);
    if (validRows.length === 0) {
      showToast({
        title: 'No valid users',
        description: 'Please fix errors in your CSV before importing',
        variant: 'error',
      });
      return;
    }

    setImportResult(null);

    try {
      // Convert parsed rows to BulkCreateUser format
      const users = validRows.map((row) => ({
        email: row.email,
        fullName: row.full_name,
        role: (row.role?.trim().toLowerCase() as UserRole) || defaultRole,
        groupImportKeys: row.groupImportKeys,
      }));

      const result = await bulkCreateUsersMutation.mutateAsync(users);
      setImportResult(result);

      if (result.failed.length === 0) {
        showToast({
          title: 'Import successful',
          description: `${result.created.length} users created successfully`,
          variant: 'success',
        });
        // Navigate back to manage-users after successful import
        setTimeout(() => {
          handleReset();
          navigate({ to: '/admin/manage-users' });
        }, 2000);
      } else {
        showToast({
          title: 'Partial success',
          description: `${result.created.length} created, ${result.failed.length} failed`,
          variant: result.created.length > 0 ? 'success' : 'error',
        });
      }
    } catch (error) {
      showToast({
        title: 'Import failed',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'error',
      });
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setParsedRows([]);
    setDefaultRole('');
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validRowsCount = previewRows.filter(
    (row) => row._errors.length === 0
  ).length;
  const hasErrors = validRowsCount !== previewRows.length;
  const groupKeyCount = new Set(
    previewRows.flatMap((row) => row.groupImportKeys)
  ).size;

  // Helper function to get the role that will be used for a row
  const getRoleForRow = (row: ParsedCSVRow): string => {
    if (row.role) {
      return getRoleLabelByKey(row.role);
    }
    return defaultRole ? getRoleLabelByKey(defaultRole) : 'Not set';
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="w-full max-w-4xl mx-auto px-4 pb-8 bg-slate-700">
          <h1 className="text-[24px] text-slate-50 mb-2">
            {ManageUsersContent.bulkImport.title}
          </h1>
          <p className="text-body text-slate-300 mb-6">
            {ManageUsersContent.bulkImport.description}
          </p>

          <div className="space-y-4">
            {/* Download Template Button */}
            <div>
              <Button
                size="lg"
                type="button"
                variant="outline"
                onClick={handleDownloadTemplate}
                className="w-full"
                disabled={bulkCreateUsersMutation.isPending}
              >
                <DownloadSimpleIcon size={20} weight="bold" />
                {ManageUsersContent.bulkImport.downloadTemplate}
              </Button>
            </div>

            {/* File Input */}
            <div className="space-y-2">
              <Label htmlFor="csv-file">
                {ManageUsersContent.bulkImport.selectFile}
              </Label>
              <input
                ref={fileInputRef}
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                disabled={bulkCreateUsersMutation.isPending}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={bulkCreateUsersMutation.isPending}
                className="w-full h-36 border-dashed"
              >
                {selectedFile
                  ? selectedFile.name
                  : ManageUsersContent.bulkImport.noFileSelected}
              </Button>
            </div>

            {/* Default Role Selector */}
            {selectedFile && (
              <div className="space-y-2">
                <Label htmlFor="default-role">
                  Default Role <span className="text-red-400">*</span>
                </Label>
                <Select
                  value={defaultRole}
                  onValueChange={(value) => setDefaultRole(value as UserRole)}
                  disabled={bulkCreateUsersMutation.isPending}
                >
                  <SelectTrigger id="default-role">
                    <SelectValue placeholder="Select default role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">
                  Used for rows without a role specified
                </p>
              </div>
            )}

            {selectedFile && (
              <div className="rounded-lg border border-green-500/40 bg-green-500/10 p-3 text-sm text-green-100">
                Use <span className="font-mono">group_import_keys</span> for
                optional starting groups. Multiple keys should be
                comma-separated inside quotes. Imports add memberships and do
                not remove existing groups.
              </div>
            )}

            {/* Preview Table */}
            {previewRows.length > 0 && (
              <div className="space-y-2">
                <Label>
                  Preview ({previewRows.length}{' '}
                  {previewRows.length === 1 ? 'row' : 'rows'}
                  {validRowsCount !== previewRows.length &&
                    `, ${validRowsCount} valid`}
                  {groupKeyCount > 0 && `, ${groupKeyCount} group keys`})
                </Label>
                <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-500">
                  <div className="divide-y divide-slate-500">
                    {previewRows.map((row, index) => {
                      const isValid = row._errors.length === 0;
                      return (
                        <div
                          key={index}
                          className="p-3 text-sm flex items-start gap-2"
                        >
                          {isValid ? (
                            <CheckCircleIcon
                              className="text-green-400 mt-0.5 flex-shrink-0"
                              size={16}
                              weight="fill"
                            />
                          ) : (
                            <XCircleIcon
                              className="text-red-400 mt-0.5 flex-shrink-0"
                              size={16}
                              weight="fill"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-50 truncate">
                              {row.email}
                            </div>
                            {row.full_name && (
                              <div className="text-slate-400 truncate">
                                {row.full_name}
                              </div>
                            )}
                            <div className="text-slate-300 text-xs mt-1">
                              Role: {getRoleForRow(row)}
                            </div>
                            {row.groupImportKeys.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {row.groupImportKeys.map((key) => (
                                  <span
                                    key={key}
                                    className="rounded border border-slate-500 bg-slate-700 px-1.5 py-0.5 font-mono text-[11px] text-slate-300"
                                  >
                                    {key}
                                  </span>
                                ))}
                              </div>
                            )}
                            {row._errors.length > 0 && (
                              <div className="space-y-0.5 text-red-400 text-xs mt-1">
                                {row._errors.map((error) => (
                                  <div key={error}>{error}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Import Button */}
            {previewRows.length > 0 && (
              <div className="space-y-2">
                <Button
                  type="button"
                  onClick={handleImport}
                  disabled={
                    bulkCreateUsersMutation.isPending ||
                    validRowsCount === 0 ||
                    hasErrors ||
                    !defaultRole
                  }
                  className="w-full"
                  size="lg"
                >
                  {bulkCreateUsersMutation.isPending
                    ? ManageUsersContent.bulkImport.importing
                    : ManageUsersContent.bulkImport.importUsers(validRowsCount)}
                </Button>
                {!bulkCreateUsersMutation.isPending &&
                  (validRowsCount === 0 || hasErrors || !defaultRole) && (
                    <p className="text-xs text-slate-400 text-center">
                      {!defaultRole
                        ? 'Please select a default role to continue'
                        : hasErrors
                          ? 'Please fix all errors in the CSV before importing'
                          : validRowsCount === 0
                            ? 'No valid rows to import'
                            : ''}
                    </p>
                  )}
              </div>
            )}

            {/* Results Display */}
            {importResult && (
              <div className="space-y-2 p-4 rounded-lg border border-slate-500 bg-slate-600">
                <div className="font-medium text-slate-50">
                  {ManageUsersContent.bulkImport.results.title}
                </div>
                {importResult.created.length > 0 && (
                  <div className="text-green-400 text-sm">
                    {ManageUsersContent.bulkImport.results.success(
                      importResult.created.length
                    )}
                  </div>
                )}
                {importResult.failed.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-red-400 text-sm">
                      {ManageUsersContent.bulkImport.results.failed(
                        importResult.failed.length
                      )}
                    </div>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {importResult.failed.map((failure, index) => (
                        <div
                          key={index}
                          className="text-xs text-red-300 pl-2 border-l-2 border-red-400"
                        >
                          <div className="font-medium">{failure.email}</div>
                          <div>{failure.reason}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
        action={toast.action}
      />
    </>
  );
}
